import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { QueryKey } from '../../../../modules/growth/types/growth-query-key';
import {
	AvailableTemplatesResponse,
	CreateReviewRecordRequest,
	DeleteReviewRecordResponse,
	FullReviewRecord,
	FullReviewRecordApiData,
	RecordsEnabledResponse,
	ReviewRecord,
	ReviewRecordApiData,
	ReviewRecordsQueryParams,
	ReviewRecordTransformer,
	UpdateReviewRecordRequest,
} from '../types';

// Re-export types for convenience
export type { FullReviewRecord } from '../types';

// API endpoints
const REVIEW_RECORDS_ENDPOINT = '/achieve/review_records';
const REVIEW_TEMPLATES_ENDPOINT = '/achieve/review_templates';

// Query keys - following the same pattern as use-goals.ts

// Hook to fetch review records list
export const useReviewRecords = (params: ReviewRecordsQueryParams = {}, options: UseQueryOptions<ReviewRecord[], unknown, ReviewRecord[]> = {}) => {
	const http = useHttpClient('v2');

	return useQuery<ReviewRecord[], unknown, ReviewRecord[]>(
		[QueryKey.ReviewRecords, params],
		async () => {
			const queryParams: Record<string, any> = {};

			if (params.limit !== undefined) queryParams.limit = params.limit;
			if (params.userId !== undefined) queryParams.user_id = params.userId;
			if (params.status !== undefined) queryParams.status = params.status;
			if (params.timePeriodStart !== undefined) queryParams.time_period_start = params.timePeriodStart;
			if (params.timePeriodEnd !== undefined) queryParams.time_period_end = params.timePeriodEnd;

			const response = await http.get<ReviewRecordApiData[]>(REVIEW_RECORDS_ENDPOINT, {
				params: queryParams,
			});

			return response.data.map(ReviewRecordTransformer.fromApiData);
		},
		{
			refetchOnWindowFocus: false,
			staleTime: 30 * 1000, // Consider data stale after 30 seconds
			...options,
		}
	);
};

// Hook to fetch a single review record
export const useReviewRecord = (id: string, options: UseQueryOptions<FullReviewRecord, unknown, FullReviewRecord> = {}) => {
	const http = useHttpClient('v2');

	return useQuery<FullReviewRecord, unknown, FullReviewRecord>(
		[QueryKey.ReviewRecord, id],
		async () => {
			const response = await http.get<FullReviewRecordApiData>(`${REVIEW_RECORDS_ENDPOINT}/${id}`);
			return ReviewRecordTransformer.fromFullApiData(response.data);
		},
		{
			refetchOnWindowFocus: false,
			staleTime: 30 * 1000,
			...options,
			enabled: !!id && options.enabled !== false,
		}
	);
};

// Hook to create a new review record
export const useCreateReviewRecord = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation<FullReviewRecord, unknown, CreateReviewRecordRequest>(
		async (request: CreateReviewRecordRequest) => {
			const payload = ReviewRecordTransformer.toCreateApiData(request);
			const response = await http.post<FullReviewRecordApiData>(REVIEW_RECORDS_ENDPOINT, payload);
			return ReviewRecordTransformer.fromFullApiData(response.data);
		},
		{
			onSuccess: () => {
				// Invalidate all review records queries to refresh the list
				queryClient.invalidateQueries({ queryKey: [QueryKey.ReviewRecords] });
			},
		}
	);
};

// Hook to update a review record
export const useUpdateReviewRecord = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation<FullReviewRecord, unknown, { id: string; request: UpdateReviewRecordRequest }>(
		async ({ id, request }) => {
			const payload = ReviewRecordTransformer.toUpdateApiData(request);
			const response = await http.put<FullReviewRecordApiData>(`${REVIEW_RECORDS_ENDPOINT}/${id}`, payload);
			return ReviewRecordTransformer.fromFullApiData(response.data);
		},
		{
			onSuccess: (data) => {
				// Update the specific record in cache
				queryClient.setQueryData([QueryKey.ReviewRecord, data.id], data);
				// Invalidate lists to ensure consistency
				queryClient.invalidateQueries({ queryKey: [QueryKey.ReviewRecords] });
			},
		}
	);
};

// Hook to delete a review record
export const useDeleteReviewRecord = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation<DeleteReviewRecordResponse, unknown, string>(
		async (id: string) => {
			const response = await http.delete<DeleteReviewRecordResponse>(`${REVIEW_RECORDS_ENDPOINT}/${id}`);
			return response.data;
		},
		{
			onSuccess: (_, id) => {
				// Remove the specific record from cache
				queryClient.removeQueries({ queryKey: [QueryKey.ReviewRecord, id] });
				// Invalidate lists to refresh
				queryClient.invalidateQueries({ queryKey: [QueryKey.ReviewRecords] });
			},
		}
	);
};

// Hook to retry generation for a failed review record
export const useRetryReviewRecord = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation<FullReviewRecord, unknown, string>(
		async (id: string) => {
			const response = await http.post<FullReviewRecordApiData>(`${REVIEW_RECORDS_ENDPOINT}/${id}/retry_generation`);
			return ReviewRecordTransformer.fromFullApiData(response.data);
		},
		{
			onSuccess: (data) => {
				// Update the specific record in cache
				queryClient.setQueryData([QueryKey.ReviewRecord, data.id], data);
				// Invalidate lists to ensure consistency
				queryClient.invalidateQueries({ queryKey: [QueryKey.ReviewRecords] });
			},
		}
	);
};

// Hook to check if review records are enabled for the company
export const useRecordsEnabled = (options: UseQueryOptions<boolean, unknown, boolean> = {}) => {
	const http = useHttpClient('v2');

	return useQuery<boolean, unknown, boolean>(
		[QueryKey.ReviewRecords, 'enabled'],
		async () => {
			const response = await http.get<RecordsEnabledResponse>(`${REVIEW_RECORDS_ENDPOINT}/records_enabled`);
			return response.data.enabled;
		},
		{
			refetchOnWindowFocus: false,
			staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
			...options,
		}
	);
};

// Hook to get available templates for creating review records
export const useAvailableTemplates = (
	options: UseQueryOptions<AvailableTemplatesResponse['templates'], unknown, AvailableTemplatesResponse['templates']> = {}
) => {
	const http = useHttpClient('v2');

	return useQuery<AvailableTemplatesResponse['templates'], unknown, AvailableTemplatesResponse['templates']>(
		[QueryKey.ReviewRecords, 'templates'],
		async () => {
			const response = await http.get<AvailableTemplatesResponse>(`${REVIEW_TEMPLATES_ENDPOINT}/available_for_records`);
			return response.data.templates;
		},
		{
			refetchOnWindowFocus: false,
			staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
			...options,
		}
	);
};

// Hook to update a specific section's answer in a review record
export const useUpdateSectionResponse = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation<void, unknown, { reviewRecordId: string; sectionId: string; answer: string }>(
		async ({ reviewRecordId, sectionId, answer }) => {
			await http.patch(`${REVIEW_RECORDS_ENDPOINT}/${reviewRecordId}/responses/${sectionId}`, {
				answer,
			});
		},
		{
			onSuccess: (_, variables) => {
				// Invalidate the specific review record to refresh its data
				queryClient.invalidateQueries({ queryKey: [QueryKey.ReviewRecord, variables.reviewRecordId] });
			},
		}
	);
};
